import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { array, dateISO, Decoder, field, number, oneOf, string, succeed } from 'jsonous';
import { resourceDecoder } from '../../Resource/Decoders';
import {
  AttributeMappingEntry,
  CapturedAttribute,
  ConnectionDetails,
  ConnectionResource,
  ConnectionSpDetails,
  ConnectionSummary,
  HubSpDetails,
  LoginDomain,
  SsoConfigurations,
  SsoConfigurationsResource,
  SsoConnectionStatus,
  SsoEnvironment,
  SsoVendor,
  TestSignInLaunch,
} from './Types';

const ssoVendorDecoder: Decoder<SsoVendor> = oneOf<SsoVendor>([
  stringLiteral<SsoVendor>('entra'),
  stringLiteral<SsoVendor>('okta'),
  stringLiteral<SsoVendor>('adfs'),
  stringLiteral<SsoVendor>('ping'),
  stringLiteral<SsoVendor>('other'),
]);

const ssoEnvironmentDecoder: Decoder<SsoEnvironment> = oneOf<SsoEnvironment>([
  stringLiteral<SsoEnvironment>('production'),
  stringLiteral<SsoEnvironment>('staging'),
]);

const ssoConnectionStatusDecoder: Decoder<SsoConnectionStatus> = oneOf<SsoConnectionStatus>([
  stringLiteral<SsoConnectionStatus>('awaiting_metadata'),
  stringLiteral<SsoConnectionStatus>('verifying'),
  stringLiteral<SsoConnectionStatus>('attributes_check'),
  stringLiteral<SsoConnectionStatus>('active'),
  stringLiteral<SsoConnectionStatus>('needs_attention'),
]);

const connectionSummaryDecoder: Decoder<ConnectionSummary> = succeed({})
  .assign('id', field('id', number))
  .assign('displayName', field('display_name', string))
  .assign('vendor', field('vendor', ssoVendorDecoder))
  .assign('environment', field('environment', ssoEnvironmentDecoder))
  .assign('status', field('status', ssoConnectionStatusDecoder))
  .assign('providerName', field('provider_name', string))
  .assign('assignedDomainCount', field('assigned_domain_count', number));

const loginDomainDecoder: Decoder<LoginDomain> = succeed({})
  .assign('id', field('id', number))
  .assign('domain', field('domain', string))
  .assign('ssoConnectionId', field('sso_connection_id', explicitMaybe(number)));

const hubSpDetailsDecoder: Decoder<HubSpDetails> = succeed({})
  .assign('entityId', field('entity_id', string))
  .assign('acsUrl', field('acs_url', string));

const ssoConfigurationsDecoder: Decoder<SsoConfigurations> = succeed({})
  .assign('connections', field('connections', array(connectionSummaryDecoder)))
  .assign('loginDomains', field('login_domains', array(loginDomainDecoder)))
  .assign('spDetails', field('sp_details', hubSpDetailsDecoder));

export const ssoConfigurationsResourceDecoder: Decoder<SsoConfigurationsResource> =
  resourceDecoder(ssoConfigurationsDecoder);

const attributeMappingEntryDecoder: Decoder<AttributeMappingEntry> = succeed({})
  .assign('field', field('field', string))
  .assign('claim', field('claim', string));

const capturedAttributeDecoder: Decoder<CapturedAttribute> = succeed({})
  .assign('name', field('name', string))
  .assign('value', field('value', string));

const connectionSpDetailsDecoder: Decoder<ConnectionSpDetails> = succeed({})
  .assign('entityId', field('entity_id', string))
  .assign('acsUrl', field('acs_url', string))
  .assign('relayState', field('relay_state', string))
  .assign('signingCertificate', field('signing_certificate', explicitMaybe(string)));

const connectionDetailsDecoder: Decoder<ConnectionDetails> = succeed({})
  .assign('id', field('id', number))
  .assign('displayName', field('display_name', string))
  .assign('vendor', field('vendor', ssoVendorDecoder))
  .assign('environment', field('environment', ssoEnvironmentDecoder))
  .assign('status', field('status', ssoConnectionStatusDecoder))
  .assign('providerName', field('provider_name', string))
  .assign('metadataUrl', field('metadata_url', explicitMaybe(string)))
  .assign('attributeMapping', field('attribute_mapping', array(attributeMappingEntryDecoder)))
  .assign('requiredAttributes', field('required_attributes', array(string)))
  .assign(
    'lastLaunchAttributes',
    field('last_launch_attributes', explicitMaybe(array(capturedAttributeDecoder))),
  )
  .assign('lastLaunchAt', field('last_launch_at', explicitMaybe(dateISO)))
  .assign('lastError', field('last_error', explicitMaybe(string)))
  .assign('verifiedAt', field('verified_at', explicitMaybe(dateISO)))
  .assign('certificateExpiresAt', field('certificate_expires_at', explicitMaybe(dateISO)))
  .assign('createdAt', field('created_at', dateISO))
  .assign('spDetails', field('sp_details', connectionSpDetailsDecoder));

export const connectionResourceDecoder: Decoder<ConnectionResource> =
  resourceDecoder(connectionDetailsDecoder);

export const testSignInLaunchDecoder: Decoder<TestSignInLaunch> = succeed({}).assign(
  'actionUrl',
  field('action_url', string),
);
